import type {
  BackendUserWithRequiredIds,
  ProtectedRouteDecision,
} from '../contexts/app-session-state'
import type { User as BackendUser } from '../dal/user-dal'
import type { User as AuthUser } from './useAuth0Adapter'
import { hasRequiredBackendUserIds } from '../contexts/app-session-state'

type AuthUserWithId = AuthUser & { sub: string }

export function hasRequiredAuthUserId(
  authUser: AuthUser | null | undefined,
): authUser is AuthUserWithId {
  return typeof authUser?.sub === 'string' && authUser.sub.length > 0
}

export interface RequiredAppSessionUser {
  authUser: AuthUserWithId
  authUserId: string
  backendUser: BackendUserWithRequiredIds
  revenueCatId: string
}

interface ResolveParams {
  protectedRoute: ProtectedRouteDecision
  user: AuthUser | null | undefined
  backendUser: BackendUser | null | undefined
}

export function resolveRequiredAppSessionUser({
  protectedRoute,
  user,
  backendUser,
}: ResolveParams): RequiredAppSessionUser {
  if (protectedRoute !== 'allow') {
    throw new Error(
      'useRequiredAppSessionUser requires the protected account route gate to be resolved.',
    )
  }

  if (!hasRequiredAuthUserId(user)) {
    throw new Error(
      'useRequiredAppSessionUser requires an authenticated Auth0 user with a stable identifier.',
    )
  }

  if (!hasRequiredBackendUserIds(backendUser)) {
    throw new Error(
      'useRequiredAppSessionUser requires a backend user with non-optional IDs.',
    )
  }

  return {
    authUser: user,
    authUserId: user.sub,
    backendUser,
    revenueCatId: backendUser.revenueCatId,
  }
}
